// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TagAlongMessage from "@west/App/Components/Product/TagAlongMessage";
import CartLineQuantity, { CartLineQuantityStyles } from "@west/Overrides/Widgets/Cart/CartLineQuantity";
import { EMPTY_VALUE } from "@west/XDL/constants";
import Account from "@west/XDL/Icons/Account";
import Remove from "@west/XDL/Icons/Remove";
import { color } from "@west/XDL/Utilities";
import React, { FC, useState } from "react";
import { HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
}

interface MiniCartLineStyles {
    container?: InjectableCss;
    descriptionContainer?: InjectableCss;
    descriptionColumnContainer?: InjectableCss;
    qtyContainer?: InjectableCss;
    priceContainer?: InjectableCss;
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productBrand?: ProductBrandStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceText?: TypographyProps;
    removeIcon?: IconPresentationProps;
    cartLineErrorMessageWrapper?: InjectableCss;
    cartLineErrorMessageText?: TypographyProps;
}

export const miniCartLineStyles: MiniCartLineStyles = {};

const styles = miniCartLineStyles;

type Props = OwnProps & HasCartLineContext;

const MiniCartLineCard: FC<Props> = ({ cart, cartLine, removeCartLine, updateCartLine }) => {
    const { enableVat, vatPriceDisplay, showPricing } = useAppSelector(state => ({
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
        showPricing: state.context.showPricing,
    }));
    // <XNG-Changes>
    const [isRelatedProduct, setIsRelatedProduct] = useState(false);
    // </XNG-Changes>

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const removeCartLineClickHandler = () => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    React.useEffect(() => {
        setIsRelatedProduct(cartLine.properties.tagAlongProduct?.trim()?.length > 0);
    }, [cartLine]);

    const errorMessages: React.ReactNode[] = [];

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    return (
        <StyledWrapper
            {...styles.container}
            className="mini-cartLine"
            data-test-selector={`mini-cartLine_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <StyledWrapper {...styles.descriptionContainer}>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                <StyledWrapper {...styles.descriptionColumnContainer}>
                    <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                    <TagAlongMessage cart={cart} />
                    {cartLine.brand && <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />}
                    <ProductPartNumbers
                        productNumber={cartLine.erpNumber}
                        customerProductNumber={cartLine.customerName}
                        manufacturerItem={cartLine.manufacturerItem}
                        extendedStyles={styles.productPartNumbers}
                        showProductNumber={false}
                    />
                    {errorMessages.length > 0 && (
                        <StyledWrapper {...styles.cartLineErrorMessageWrapper}>
                            {errorMessages.map((message, index) => (
                                <Typography
                                    {...styles.cartLineErrorMessageText}
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    data-test-selector="cartline_errorMessage"
                                >
                                    {message}
                                </Typography>
                            ))}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.qtyContainer}>
                <CartLineQuantity
                    cart={cart}
                    editable={!isRelatedProduct}
                    onQtyOrderedChange={qtyOrderedChangeHandler}
                    extendedStyles={styles.quantity}
                    isMiniCart={true}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.priceContainer}>
                {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && showPricing ? (
                    <Typography {...styles.extendedUnitNetPriceText} data-test-selector="cartline_extendedUnitNetPrice">
                        {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                            ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                            : cartLine.pricing.extendedUnitNetPriceDisplay}
                    </Typography>
                ) : (
                    <Typography {...styles.extendedUnitNetPriceText} data-test-selector="cartline_extendedUnitNetPrice">
                        {EMPTY_VALUE}
                    </Typography>
                )}
                {!isRelatedProduct && <Icon {...styles.removeIcon} onClick={removeCartLineClickHandler} />}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withCartLine(MiniCartLineCard);
